import { useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { rupiah } from "@/lib/api";

const WEEKDAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const short = (v) => (v >= 1e6 ? `${(v / 1e6).toFixed(1)}jt` : v >= 1e3 ? `${Math.round(v / 1e3)}rb` : v);

const ChartCard = ({ title, peak, data, x, metric, testid }) => (
  <div className="card-paper p-5" data-testid={testid}>
    <div className="flex flex-wrap justify-between gap-2 mb-3">
      <h3 className="font-semibold">{title}</h3>
      {peak && <span className="text-xs px-2.5 py-1 rounded-full bg-sage-100 text-forest font-semibold" data-testid={`${testid}-peak`}>Tersibuk: {peak}</span>}
    </div>
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ left: -10, right: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#DCEAF7" />
          <XAxis dataKey={x} tick={{ fontSize: 11 }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={metric === "total" ? short : undefined} allowDecimals={false} />
          <Tooltip formatter={(v) => (metric === "total" ? rupiah(v) : `${v} pesanan`)} labelStyle={{ color: "#173A60" }} />
          <Bar dataKey={metric} name={metric === "total" ? "Penjualan" : "Pesanan"} fill="#155EAD" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const peakOf = (rows, label, metric) => {
  const top = rows.reduce((a, b) => (b[metric] > (a?.[metric] || 0) ? b : a), null);
  if (!top) return null;
  return `${label(top)} (${metric === "total" ? rupiah(top.total) : `${top.orders} pesanan`})`;
};

export const SalesCharts = ({ data }) => {
  const [metric, setMetric] = useState("orders");
  const hours = data.by_hour.map((h) => ({ ...h, label: `${String(h.hour).padStart(2, "0")}` }));
  const weekdays = data.by_weekday.map((w) => ({ ...w, label: WEEKDAYS[w.weekday] }));
  const days = data.by_date.map((d) => ({ ...d, label: d.date.slice(8) }));
  const btn = (m, t) => (
    <button onClick={() => setMetric(m)} className={`px-3 py-1.5 text-xs rounded-full font-semibold ${metric === m ? "bg-forest text-white" : "bg-sage-100 text-ink"}`} data-testid={`chart-metric-${m}`}>{t}</button>
  );
  return (
    <div className="mt-6" data-testid="sales-charts">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-serif font-semibold text-xl">Grafik penjualan</h3>
        <div className="flex gap-2">{btn("orders", "Jumlah pesanan")}{btn("total", "Nilai penjualan")}</div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Per jam (WIB)" testid="chart-hourly" data={hours} x="label" metric={metric} peak={peakOf(hours, (h) => `${h.label}.00–${h.label}.59`, metric)} />
        <ChartCard title="Per hari dalam seminggu" testid="chart-weekday" data={weekdays} x="label" metric={metric} peak={peakOf(weekdays, (w) => w.label, metric)} />
        <div className="lg:col-span-2">
          <ChartCard title="Per tanggal" testid="chart-daily" data={days} x="label" metric={metric} peak={peakOf(days, (d) => `tgl ${Number(d.label)}`, metric)} />
        </div>
      </div>
    </div>
  );
};
